<template>
    <flex-container
        v-if="filteredUsers.length > 0"
        :gap="Size.sm"
        direction="column"
    >
        <multi-select-input
            :items="filteredUsers"
            searchable
            :search-filter="(user, search) => searchFilter(user, search)"
        >
            <template #active>
                <span>Kies je gebruiker</span>
                <svg-icon
                    :icon="ExpandIcon"
                    :color="'gray-400'"
                    :size="IconSize.sm"
                    class="expand-icon"
                />
            </template>
            <template #item="{ bindings: itemBindings }">
                <span style="display:flex; align-items: center;">
                    <user-avatar
                        :placeholder="itemBindings.item.name.abbreviation"
                        size="md"
                        :url="itemBindings.item.avatar ? resizedToSizeUploadUrl(itemBindings.item.avatar, 'md') : ''"
                        :alt="itemBindings.item.name"
                    />
                    <span>&nbsp;{{ itemBindings.item.name }}</span>
                </span>
            </template>
        </multi-select-input>
        <chip-container
            v-if="inputBindings && props.showChips && inputBindings.value.value.length > 0"
        >
            <chip-label
                v-for="(user) in inputBindings?.value.value"
                :key="user.id"
                size="lg"
                :background-color="'gray-50'"
                :border-color="'gray-300'"
            >
                <text-decorator :color="'gray-800'">
                    {{ user.email }}
                </text-decorator>
                <button-plain-icon
                    :size="IconSize.sm"
                    :icon="CloseIcon"
                    :color="'gray-400'"
                    @click="removeUser(user)"
                />
            </chip-label>
        </chip-container>
    </flex-container>
</template>

<script setup lang="ts">
    import ExpandIcon from '@keyvue/one-material-icons/icons/expand_less.svg?component';
    import CloseIcon from '@keyvue/one-material-icons/icons/close.svg?component';
    import { computed, inject, onMounted, ref } from 'vue';
    import { User } from '@keyvue/one-api-client';
    import { resizedToSizeUploadUrl } from '@/services/keyvue-api/utils/resize';
    import { IconSize, Size } from '@keyvue/one-ui';
    import useClient from '@/services/keyvue-api/composables/useClient';

    const { client, currentUser } = useClient();

    const props = withDefaults(defineProps<{
        filter?: (userCollection: any) => boolean,
        withoutCurrentUser?: boolean,
        showChips?: boolean
    }>(), {
        filter: undefined,
        showChips: false,
    });

    const userCollection = ref<{ data: User[], total: number }>({ data: [], total: 0 });

    async function loadUsers() {
        if (!currentUser.value || !currentUser.value.company) {
            return;
        }

        const params = {
            'active': true,
            'order[name.firstName]': 'asc',
        };

        userCollection.value = await client.getSubResources(
            currentUser.value.company,
            User,
            'users',
            params,
        );
    }

    const filteredUsers = computed(() => {
        let users = userCollection.value.data;

        if (props.withoutCurrentUser) {
            users = users.filter((user) => !user.id.equals(currentUser.value.id));
        }

        if (props.filter !== undefined) {
            users = users.filter(props.filter);
        }

        return users;
    });

    const inputBindings: {
        value: {
            value: User[]
        },
        setValue: (newValue: any) => void,
    } | undefined = inject('input-bindings');

    function removeUser(user: User) {
        if (!inputBindings) {
            return;
        }

        const selectedUsers = inputBindings.value.value;
        const userIndex = selectedUsers.findIndex((findableUser) => (user.equals(findableUser)));
        selectedUsers.splice(userIndex, 1);

        inputBindings.setValue(selectedUsers);
    }

    function searchFilter(user: User | undefined, search: string | null): boolean {
        const term = search?.toLowerCase();
        if (term === undefined) {
            return true;
        }

        return (
            (user?.email?.value?.toLowerCase() ?? '').includes(term)
            || (user?.name?.toString().toLowerCase() ?? '').includes(term)
        );
    }

    onMounted(() => {
        loadUsers();
    });
</script>
